import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings, User, Link2, Bell, Settings2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileSettingsTab } from './ProfileSettingsTab';
import { IntegrationsTab } from './IntegrationsTab';
import { NotificationsTab } from './NotificationsTab';
import { SystemSettingsTab } from './SystemSettingsTab';
import { useSharePointAuth } from '@/hooks/useSharePointAuth';

interface SettingsLayoutProps {
  showSystemTab: boolean;
}

interface TabDefinition {
  id: string;
  label: string;
  icon: typeof User;
  adminOnly?: boolean;
}

const TABS: TabDefinition[] = [
  { id: 'profile', label: 'Profiel', icon: User },
  { id: 'integrations', label: 'Integraties', icon: Link2 },
  { id: 'notifications', label: 'Notificaties', icon: Bell },
  { id: 'system', label: 'Systeem', icon: Settings2, adminOnly: true },
];

type TabId = 'profile' | 'integrations' | 'notifications' | 'system';

export function SettingsLayout({ showSystemTab }: SettingsLayoutProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isConnected: isSharePointConnected } = useSharePointAuth();
  
  const currentTab = (searchParams.get('tab') as TabId) || 'profile';
  
  // Filter tabs based on admin status
  const visibleTabs = TABS.filter(tab => !tab.adminOnly || showSystemTab);
  
  // Validate current tab is visible
  const validTab = visibleTabs.some(tab => tab.id === currentTab) ? currentTab : 'profile';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      const currentIndex = visibleTabs.findIndex(tab => tab.id === validTab);
      
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        handleTabChange(visibleTabs[currentIndex - 1].id);
      } else if (e.key === 'ArrowRight' && currentIndex < visibleTabs.length - 1) {
        handleTabChange(visibleTabs[currentIndex + 1].id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [validTab, visibleTabs]);

  return (
    <div className="container mx-auto py-4 sm:py-6 px-4 sm:px-6 max-w-4xl">
      <div className="flex items-center gap-2 mb-4 sm:mb-6">
        <Settings className="h-5 w-5 sm:h-6 sm:w-6" />
        <h1 className="text-2xl sm:text-3xl font-bold">Instellingen</h1>
      </div>

      <Tabs value={validTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-full justify-start mb-6 h-auto flex-wrap gap-1 bg-muted/50 p-1">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const showBadge = tab.id === 'integrations' && !isSharePointConnected;
            
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex items-center gap-2 px-3 py-2 data-[state=active]:bg-background relative"
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                {showBadge && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-warning" />
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="profile" className="mt-0">
          <ProfileSettingsTab />
        </TabsContent>

        <TabsContent value="integrations" className="mt-0">
          <IntegrationsTab />
        </TabsContent>

        <TabsContent value="notifications" className="mt-0">
          <NotificationsTab />
        </TabsContent>

        {showSystemTab && (
          <TabsContent value="system" className="mt-0">
            <SystemSettingsTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
